/**
 * @module PackChallenge
 * @description "Défi de la Meute" — Weekly collective/individual challenge
 * with animated progress bar, leaderboard, join button, history,
 * and victory confetti celebration.
 */
import React, { useState, useEffect, useCallback, memo } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  Target, Users, Flame, Droplets, Dumbbell, UtensilsCrossed,
  Zap, Swords, Trophy, Crown, ChevronDown, ChevronUp,
  Loader2, CheckCircle2, Shield, Clock, Medal, Star
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const METRIC_ICONS = {
  meals_logged: UtensilsCrossed,
  protein_days: Zap,
  hydration_days: Droplets,
  workout_count: Dumbbell,
  streak_days: Flame,
  total_workouts: Swords,
  custom: Target,
};

const METRIC_LABELS = {
  meals_logged: { fr: 'repas logues', en: 'meals logged' },
  protein_days: { fr: 'jours protéines', en: 'protein days' },
  hydration_days: { fr: 'jours hydratation', en: 'hydration days' },
  workout_count: { fr: 'workouts', en: 'workouts' },
  streak_days: { fr: 'jours consecutifs', en: 'streak days' },
  total_workouts: { fr: 'workouts collectifs', en: 'collective workouts' },
  custom: { fr: 'points', en: 'points' },
};

function ChallengeProgress({ progress, target, pct, completed, metric, language }) {
  const fr = language === 'fr';
  const label = METRIC_LABELS[metric] || METRIC_LABELS.custom;
  const barColor = completed
    ? 'from-green-500 to-emerald-400'
    : pct >= 75
      ? 'from-orange-500 to-amber-400'
      : pct >= 50
        ? 'from-yellow-500 to-amber-400'
        : 'from-primary to-blue-400';

  return (
    <div className="space-y-2" data-testid="challenge-progress-bar">
      <div className="flex items-end justify-between">
        <span className="font-heading text-2xl font-black text-foreground">
          {progress}
          <span className="text-base text-muted-foreground font-normal">/{target}</span>
        </span>
        <span className="text-xs text-muted-foreground">{fr ? label.fr : label.en}</span>
      </div>
      <div className="h-4 rounded-full bg-secondary overflow-hidden">
        <motion.div
          className={`h-full rounded-full bg-gradient-to-r ${barColor}`}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(pct, 100)}%` }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{pct}%</span>
        {completed ? (
          <span className="text-green-400 font-bold uppercase flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> {fr ? 'Mission accomplie!' : 'Mission Complete!'}
          </span>
        ) : (
          <span>{target - progress} {fr ? 'restant(s)' : 'remaining'}</span>
        )}
      </div>
    </div>
  );
}

function MiniLeaderboard({ leaderboard, language }) {
  if (!leaderboard || leaderboard.length === 0) return null;
  const fr = language === 'fr';

  const getRankStyle = (rank) => {
    if (rank === 1) return 'text-yellow-400';
    if (rank === 2) return 'text-gray-300';
    if (rank === 3) return 'text-amber-600';
    return 'text-muted-foreground';
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-3.5 h-3.5 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-3.5 h-3.5 text-gray-300" />;
    if (rank === 3) return <Medal className="w-3.5 h-3.5 text-amber-600" />;
    return <span className="text-[10px] w-3.5 text-center">{rank}</span>;
  };

  return (
    <div className="space-y-1.5" data-testid="challenge-leaderboard">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        <Trophy className="w-3 h-3" /> {fr ? 'Top Contributeurs' : 'Top Contributors'}
      </p>
      {leaderboard.slice(0, 5).map((p, i) => (
        <div key={p.user_id || i} className="flex items-center gap-2 py-1">
          <div className="w-5 flex justify-center">{getRankIcon(i + 1)}</div>
          <div className={`w-6 h-6 rounded-full bg-primary/15 flex items-center justify-center text-[10px] font-bold ${getRankStyle(i + 1)}`}>
            {(p.user_name || '?')[0].toUpperCase()}
          </div>
          <span className={`flex-1 text-sm truncate ${i === 0 ? 'font-bold text-foreground' : 'text-muted-foreground'}`}>
            {p.user_name || (fr ? 'Anonyme' : 'Anonymous')}
          </span>
          <span className={`font-heading text-sm font-bold ${getRankStyle(i + 1)}`}>{p.progress || 0}</span>
        </div>
      ))}
    </div>
  );
}

function ChallengeHistory({ language }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const fr = language === 'fr';

  useEffect(() => {
    api.get('/challenges/collective/history?limit=5')
      .then(res => setHistory(res.data.challenges || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || history.length === 0) return null;
  const past = history.filter(c => c.status !== 'active');
  if (past.length === 0) return null;

  return (
    <div className="mt-3" data-testid="challenge-history">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <Clock className="w-3 h-3" />
        {fr ? `Historique (${past.length})` : `History (${past.length})`}
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-2 space-y-2">
              {past.slice(0, 5).map(c => (
                <div key={c.challenge_id} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/40 text-xs">
                  {c.completed || c.progress_pct >= 100 ? (
                    <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                  ) : (
                    <Shield className="w-4 h-4 text-red-400 shrink-0" />
                  )}
                  <span className="flex-1 truncate text-foreground">{c.title}</span>
                  <span className="text-muted-foreground shrink-0">{c.progress_pct || 0}%</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


const PackChallenge = memo(function PackChallenge({ compact = false }) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const fr = language === 'fr';

  const fetchChallenge = useCallback(async () => {
    try {
      const res = await api.get('/challenges/collective/active');
      setData(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchChallenge(); }, [fetchChallenge]);

  const handleJoin = async () => {
    if (!data?.challenge?.challenge_id) return;
    setJoining(true);
    try {
      await api.post(`/challenges/collective/${data.challenge.challenge_id}/join`);
      await fetchChallenge();
    } catch { /* ignore */ }
    finally { setJoining(false); }
  };

  // Trigger confetti on 100% completion
  useEffect(() => {
    if (data?.challenge?.completed) {
      try {
        import('canvas-confetti').then(mod => {
          const confetti = mod.default;
          confetti({ particleCount: 150, spread: 90, origin: { y: 0.6 }, colors: ['#FFD700', '#FF6B35', '#22C55E'] });
          setTimeout(() => confetti({ particleCount: 80, spread: 120, origin: { y: 0.4 } }), 500);
        });
      } catch { /* no confetti */ }
    }
  }, [data?.challenge?.completed]);

  if (loading) {
    return (
      <Card className="bg-card border-border animate-pulse">
        <CardContent className="p-6 h-32 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!data?.has_active || !data?.challenge) {
    return (
      <Card className="bg-card/50 border-border" data-testid="pack-challenge-empty">
        <CardContent className="p-5 text-center">
          <Target className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
          <p className="text-sm text-muted-foreground">
            {fr ? 'Aucun défi actif cette semaine. Le prochain arrive lundi!' : 'No active challenge this week. Next one drops Monday!'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const ch = data.challenge;
  const MetricIcon = METRIC_ICONS[ch.metric] || Target;
  const isParticipant = ch.participants?.includes(user?.user_id);

  if (compact) {
    // Compact mode for Dashboard widget
    return (
      <Card className={`border-border overflow-hidden ${ch.completed ? 'bg-green-500/5 border-green-500/30' : 'bg-card/50'}`} data-testid="pack-challenge-compact">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${ch.completed ? 'bg-green-500/20' : 'bg-primary/15'}`}>
              <MetricIcon className={`w-4 h-4 ${ch.completed ? 'text-green-400' : 'text-primary'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-sm uppercase tracking-tight truncate">{ch.title}</p>
              <p className="text-[10px] text-muted-foreground">{fr ? 'Défi de la communaute' : 'Community Challenge'}</p>
            </div>
            <span className={`font-heading text-lg font-black ${ch.completed ? 'text-green-400' : 'text-primary'}`}>{ch.progress_pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <motion.div
              className={`h-full rounded-full bg-gradient-to-r ${ch.completed ? 'from-green-500 to-emerald-400' : 'from-primary to-blue-400'}`}
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(ch.progress_pct, 100)}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full mode for Community page
  return (
    <Card className={`border-border overflow-hidden ${ch.completed ? 'bg-green-500/5 border-green-500/30' : 'bg-card/50'}`} data-testid="pack-challenge">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${ch.completed ? 'bg-green-500/20 ring-2 ring-green-500/30' : 'bg-primary/15'}`}>
              <MetricIcon className={`w-6 h-6 ${ch.completed ? 'text-green-400' : 'text-primary'}`} />
            </div>
            <div>
              <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                {ch.title}
                {ch.completed && <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />}
              </CardTitle>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Users className="w-3 h-3" />
                {fr ? 'Défi de la communaute' : 'Community Challenge'}
                {ch.type === 'collective' ? (fr ? ' — Collectif' : ' — Collective') : (fr ? ' — Individuel' : ' — Individual')}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-muted-foreground">{ch.bonus_pts} pts</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{ch.description}</p>

        <ChallengeProgress
          progress={ch.progress}
          target={ch.target}
          pct={ch.progress_pct}
          completed={ch.completed}
          metric={ch.metric}
          language={language}
        />

        {/* Join button */}
        {!isParticipant && !ch.completed && (
          <Button
            onClick={handleJoin}
            disabled={joining}
            className="w-full bg-primary text-primary-foreground font-heading uppercase tracking-wider"
            data-testid="challenge-join-btn"
          >
            {joining ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Swords className="w-4 h-4 mr-2" />}
            {fr ? 'Rejoindre le défi!' : 'Join the challenge!'}
          </Button>
        )}

        {isParticipant && !ch.completed && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/10 text-xs text-primary">
            <CheckCircle2 className="w-4 h-4" />
            {fr ? 'Tu fais partie de l\'equipe!' : "You're on the team!"}
            {data.user_progress && (
              <span className="ml-auto font-bold">{fr ? 'Ta contrib:' : 'Your contrib:'} {data.user_progress.progress || 0}</span>
            )}
          </div>
        )}

        <MiniLeaderboard leaderboard={ch.individual_leaderboard} language={language} />
        <ChallengeHistory language={language} />

        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span>{fr ? 'Semaine:' : 'Week:'} {ch.week_start} → {ch.week_end}</span>
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" /> {ch.participants?.length || 0} {fr ? 'participant(s)' : 'participant(s)'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
});

export default PackChallenge;
